import { Form } from "antd";
import type { Rule } from "antd/es/form";
import { VLSegmented } from "views/uikit/VLSegmented/VLSegmented";
import styles from "./style.module.scss";
import { FileIcon } from "assets/icons/FileIcon";
import { FolderOutlined, GlobalOutlined } from "@ant-design/icons";
import SquareInfoIcon from "assets/icons/SquareInfoIcon";
import { Tooltip } from "antd";
import { validateBucket } from "helpers/validations";
import XIcon from "assets/icons/XIcon";
import { VLInput } from "views/uikit/FormFields/VLInput/VLInput";

const SOURCE_OPTIONS = [
  {
    label: "Single Folder Path",
    value: "folder",
    icon: <FolderOutlined />,
  },
  {
    label: "File List - TXT/CSV/Parquet",
    value: "filelist",
    icon: <FileIcon />,
  },
  {
    label: "Private Amazon S3 URI",
    value: "s3",
    icon: <GlobalOutlined />,
  },
];

const SUPPORTED_EXTENSIONS = [".txt", ".csv", ".parquet"];

const PLACEHOLDER_TEXT: Record<string, string> = {
  folder: "(e.g., project1/images)",
  filelist:
    "(e.g., project1/images/filelist.txt or filelist.csv or filelist.parquet)",
  s3: "(e.g., s3://bucket-name/folderA/folderB)",
};

const TOOLTIP_TEXT: Record<string, string> = {
  folder: "Avoid using dots (.) in the folder name",
  filelist:
    "Single file with file paths inside. Must end with .txt, .csv, or .parquet extensions.",
  s3: "Ensure the path is accessible from your environment.",
};

const AddDataContent = () => {
  const form = Form.useFormInstance();
  const selectedSource = Form.useWatch("sourceType", form) || "folder";

  const getPathValidationRules = () => {
    const URL_PATTERN = /^(https?|s3):\/\//i;
    const INVALID_URL_ERROR =
      "URLs (http, https, s3) are not allowed for this selection";
    const FOLDER_PATH_PATTERN =
      /^(?:[a-zA-Z]:\\)?[^<>:"|?*]*(?:\\[^<>:"|?*]+)*[\\/]?$|^\/[^<>:"|?*]*(?:\/[^<>:"|?*]+)*[\\/]?$/;

    const validators: Record<string, (value: string) => Promise<void>> = {
      folder: async (trimmed: string) => {
        if (URL_PATTERN.test(trimmed)) throw new Error(INVALID_URL_ERROR);

        // Remove trailing slashes for extension check
        const pathWithoutTrailingSlash = trimmed.replace(/[\\/]+$/, "");
        // Check if path ends with a file extension (dot followed by alphanumeric characters)
        if (/\.\w+$/.test(pathWithoutTrailingSlash)) {
          throw new Error(
            "Folder path should not contain file extensions. Avoid using dots (.) in the folder name.",
          );
        }

        if (!FOLDER_PATH_PATTERN.test(trimmed)) {
          throw new Error(
            "Invalid folder path. Please enter a valid Windows or Unix path.",
          );
        }
      },

      filelist: async (trimmed: string) => {
        if (URL_PATTERN.test(trimmed)) throw new Error(INVALID_URL_ERROR);

        const lower = trimmed.toLowerCase();
        const hasValidExtension = SUPPORTED_EXTENSIONS.some((ext) =>
          lower.endsWith(ext),
        );

        if (!hasValidExtension) {
          throw new Error(
            "Invalid file format. Supported: .txt, .csv, .parquet.",
          );
        }
      },

      s3: async (trimmed: string) => {
        if (!validateBucket(trimmed)) {
          throw new Error(
            "Invalid S3 URI format. Expected s3://bucket-name/folderA/folderB.",
          );
        }
      },
    };

    return [
      {
        validator: async (_: Rule, value: string) => {
          const trimmed = value?.trim() || "";

          if (!trimmed) {
            return Promise.reject(new Error("Path cannot be empty"));
          }

          const validate = validators[selectedSource];
          if (!validate) {
            return Promise.reject(
              new Error("Please select a valid source type."),
            );
          }

          return validate(trimmed);
        },
      },
    ];
  };

  const handleSelectedSourceChange = (value: string) => {
    form.setFieldsValue({ sourceType: value, path: "" });
  };

  return (
    <div className={styles.stepContent}>
      <div className={styles.stepHeader}>Select Data Source</div>

      {/* Source Selector */}
      <div className={styles.sourceSelection}>
        <div className={styles.sourceSelectionHeader}>Source</div>
        <Form.Item name="sourceType">
          <VLSegmented
            options={SOURCE_OPTIONS.map((opt) => ({
              label: (
                <div className={styles.sourceOption}>
                  {opt.icon}
                  {opt.label}
                </div>
              ),
              value: opt.value,
            }))}
            size="large"
            className={styles.sourceSegmented}
            onChange={(val) => handleSelectedSourceChange(val as string)}
          />
        </Form.Item>
      </div>

      {/* Path Input Section */}
      <div className={styles.pathInputSection}>
        <div className={styles.pathInputHeader}>
          <span>Enter Path</span>
          <Tooltip title={TOOLTIP_TEXT[selectedSource]}>
            <div className={styles.infoIcon}>
              <SquareInfoIcon />
            </div>
          </Tooltip>
        </div>

        <Form.Item
          name="path"
          rules={getPathValidationRules()}
          dependencies={["sourceType"]}
          style={{ marginBottom: 0 }}
          help=""
        >
          <VLInput
            type="text"
            placeholder={PLACEHOLDER_TEXT[selectedSource]}
            className={styles.pathInput}
          />
        </Form.Item>
        <Form.Item shouldUpdate={true} noStyle>
          {() => {
            const errors = form.getFieldError("path");
            const error = errors?.[0];
            return error ? (
              <span className={styles.errorMessage}>
                <XIcon />
                {error}
              </span>
            ) : null;
          }}
        </Form.Item>
      </div>
    </div>
  );
};

export default AddDataContent;
